import {AxiosError} from "axios";
import {errorText} from "../scan-operations/error/ErrorText.ts";

interface Props {
    /** Fatal error that aborted the scan, if any. */
    fatalError?: unknown
    /** Already-formatted error messages collected during the scan. */
    errors: string[]
}

interface Hint {
    title: string
    body: string
}

function classifyError(error: unknown): Hint[] {
    const hints: Hint[] = []

    if (error instanceof AxiosError) {
        const status = error.response?.status
        const code = error.code

        if (!error.response) {
            hints.push({
                title: "No response was received from METL",
                body: "This usually means the request never reached the METL server, or the server didn't return anything. Common causes: VPN/firewall blocking access, the METL host is down, DNS not resolving, or a CORS / mixed-content rejection by the browser."
            })
        }
        if (code === "ECONNABORTED") {
            hints.push({
                title: "The request timed out",
                body: "The METL server did not respond in time. Try lowering the rate limit, scanning fewer projects/versions at once, or checking the METL server's load."
            })
        }
        if (code === "ERR_NETWORK") {
            hints.push({
                title: "Network failure reaching METL",
                body: "Confirm you can hit the METL URL directly in a browser tab. If the METL URL is HTTPS, make sure this page is also loaded over HTTPS — browsers block mixed content."
            })
        }
        if (status === 401 || status === 403) {
            hints.push({
                title: "Authentication / authorization rejected",
                body: "Re-check the METL username and password on the connection step. The user must have permission to read the projects/versions being scanned."
            })
        }
        if (status === 404) {
            hints.push({
                title: "METL returned 404 Not Found",
                body: "The endpoint path may be wrong, or the resource (group/project/version) may have been renamed or deleted. Confirm the base URL ends in /rest/v1 and the resource still exists."
            })
        }
        if (status === 429) {
            hints.push({
                title: "METL is rate-limiting requests",
                body: "Lower the rate limit on the connection step (default is 20 req/sec) and try again."
            })
        }
        if (status && status >= 500) {
            hints.push({
                title: `METL returned ${status}`,
                body: "This is a server-side error in METL. Check the METL server logs around the time of the failure — there will usually be a stack trace."
            })
        }
    }

    return hints
}

export function ScanDiagnosticInfo({fatalError, errors}: Props) {
    const hints = fatalError ? classifyError(fatalError) : []
    const fatalText = fatalError ? errorText(fatalError) : null

    if (!fatalText && errors.length === 0) return null

    return <div style={{textAlign: "left", marginTop: "0.5em"}}>
        {fatalText && <p style={{margin: "0.25em 0"}}>
            <strong>Reported error:</strong> <code>{fatalText}</code>
        </p>}

        {hints.length > 0 && <>
            <h4 style={{marginBottom: "0.25em"}}>What this most likely means</h4>
            <ul style={{margin: "0.25em 0 1em 1.25em"}}>
                {hints.map((h, i) => <li key={i}>
                    <strong>{h.title}.</strong> {h.body}
                </li>)}
            </ul>
        </>}

        {errors.length > 0 && <details open>
            <summary>
                <strong>All errors recorded during this scan ({errors.length})</strong>
            </summary>
            <pre style={{
                maxHeight: "20em",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #ddd",
                padding: "0.5em",
                marginTop: "0.5em",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
            }}>
                {errors.join("\n")}
            </pre>
        </details>}

        <details>
            <summary><strong>How to gather further diagnostic information for support</strong></summary>
            <ol style={{marginTop: "0.5em"}}>
                <li>
                    Open your browser's developer tools (<kbd>F12</kbd>, or
                    <kbd>Cmd</kbd>+<kbd>Opt</kbd>+<kbd>I</kbd> on macOS) and
                    switch to the <strong>Network</strong> tab.
                </li>
                <li>
                    Tick <strong>Preserve log</strong> so requests aren't
                    cleared between page reloads, then refresh the page and re-run the scan so the
                    failing requests are captured.
                </li>
                <li>
                    Once the scan has failed, right-click anywhere inside the
                    request list and choose
                    <strong> "Save all as HAR with content"</strong> (Chrome /
                    Edge) or <strong>"Save All As HAR"</strong> (Firefox /
                    Safari). This produces a single <code>.har</code> file
                    containing every request and response from the scan — this
                    will help support to diagnose the problem.
                </li>
                <li style={{
                    background: "#fff8e1",
                    border: "1px solid #e0c060",
                    padding: "0.5em",
                    margin: "0.5em 0"
                }}>
                    <strong>⚠ The HAR file may contain sensitive data.</strong>
                    {" "}It includes request URLs, headers (which can include
                    authentication tokens or basic-auth credentials), cookies
                    and full response bodies — including the contents of jobs,
                    credentials and environment variables returned by METL.
                    Review the file before sharing it, and only send it through
                    a secure channel agreed with support.
                </li>
                <li>
                    Switch to the <strong>Console</strong> tab and copy any red
                    error lines (right-click → "Save as…" also works).
                </li>
                <li>
                    Copy the scan log shown above the error banner — it lists
                    which step was running and which group / project / version
                    was being scanned when the failure occurred.
                </li>
                <li>
                    Note the METL base URL (without credentials), browser
                    version, and whether the user account has Server Admin and Global Project Admin.
                </li>
            </ol>
            <p>
                If only one project is failing, retry with that project
                deselected — partial results are still useful.
            </p>
        </details>
    </div>
}
